import { getAiAgentMissionNumber } from "./aiAgentConfig";
import { OpportunityTracker, buildAgentDecision } from "./aiAgentObservation";
import {
  MISSION0_PACKAGE_DESCRIPTIONS,
  MISSION3_PACKAGE_DESCRIPTIONS,
  describeCue,
} from "./aiAgentPublicCues";

/**
 * The "+N" (or "-N") the game shows over the driver car after a delivery (game.js's
 * displayScoreOnVehicle/displayScoreOnVehicleM3), read from the screen rather than
 * game.rewardPoints (the configured value, known from the start of the trial). null unless it is
 * actually showing - a "+0" the player sees is a real 0.
 */
export function readShownRewardPoints() {
  const scoreEl = document.getElementById("driverScore");
  if (!scoreEl || scoreEl.classList.contains("invisible")) return null;
  const match = /^\s*([+-]?\d+)\s*$/.exec(scoreEl.textContent ?? "");
  return match ? Number(match[1]) : null;
}

// Never the raw file name: Mission 0 names (package_low.svg, ...) encode the hidden reward tier.
function packageVisualId(missionNumber, packageFile) {
  if (missionNumber === 0) return describeCue(MISSION0_PACKAGE_DESCRIPTIONS, packageFile);
  if (missionNumber === 3) return describeCue(MISSION3_PACKAGE_DESCRIPTIONS, packageFile);
  return null;
}

/** Snapshot off a live Game. Never reads trialConfig.correctKeycode (the answer key). */
function readGameSnapshot(game, session, opportunities) {
  if (!game) return null;

  const trialConfig = game.trialConfig || {};
  const missionNumber = getAiAgentMissionNumber();
  const score = typeof game.score?.getScore === "function" ? game.score.getScore() : null;
  // trial_index only exists on jsPsych data rows, not getCurrentTrial() - read off the last row.
  const lastTrialData =
    typeof game.jsPsych?.data?.get === "function" ? game.jsPsych.data.get().last(1).values()[0] : null;
  const trialIndex = lastTrialData?.trial_index ?? null;
  const { legalActions, agentObservation } = buildAgentDecision(game, missionNumber, trialIndex);
  const actionOpportunityId = opportunities.idFor(game, agentObservation, legalActions);

  return {
    // Unix epoch ms, as the agent contract asks - performance.now() counts from page load and
    // can't be joined with the agent server's logs.
    capturedAt: Date.now(),
    session: session
      ? {
          controllerType: session.controllerType,
          agentId: session.agentId,
          modelVersion: session.modelVersion,
          configurationVersion: session.configurationVersion,
          agentRunId: session.agentRunId,
        }
      : null,
    missionNumber,
    trialIndex,
    isTutorial: !!game.isTutorial,
    phase: game.gamePhase ?? null,
    // Raw geometry (phase 1 only), not a pre-baked "press now" flag - matches the inputs
    // game.js's checkProximityZone() reads. inProximityZone is that same comparison precomputed.
    proximityThresholdPx: game.targetDistance ?? null,
    inProximityZone: typeof game.checkProximityZone === "function" ? game.checkProximityZone() : null,
    driver: {
      lanePx: game.driverCarCoordinates?.left ?? null,
      speed: game.driverCarSpeed ?? null,
      topPx: game.driverCar ? parseInt(window.getComputedStyle(game.driverCar).top, 10) : null,
    },
    target: {
      lanePx: game.targetCarCoordinates?.left ?? null,
      speed: game.targetCarSpeed ?? null,
      topPx: game.targetCar ? parseInt(window.getComputedStyle(game.targetCar).top, 10) : null,
      heightPx: game.targetCar ? parseInt(window.getComputedStyle(game.targetCar).height, 10) : null,
    },
    package: {
      speed: game.packageSpeed ?? null,
      visualId: packageVisualId(missionNumber, trialConfig.packageFile),
      rewardPoints: readShownRewardPoints(),
    },
    stimulus: {
      agentFile: trialConfig.agentFile ?? null, // rendered cue, not a decoded category
    },
    legalActions,
    // Only present while the agent has a choice open (see aiAgentObservation.js); undefined
    // drops them from the JSON.
    agentObservation: agentObservation ?? undefined,
    actionOpportunityId: actionOpportunityId ?? undefined,
    csi: game.csi ?? null,
    score,
  };
}

// Excludes capturedAt so a fresh timestamp alone doesn't count as a change.
function snapshotFingerprint(snapshot) {
  if (!snapshot) return null;
  return JSON.stringify(snapshot, (key, value) => (key === "capturedAt" ? undefined : value));
}

// Puts the open choice in the log line itself, e.g. " (respond: press_key_1, ...)", so the console
// filter can find it - the filter doesn't search inside the logged object. Mission 3's lane choice
// has no observation phase, so it shows as "lane_choice".
function choiceLabel(snapshot) {
  const observation = snapshot?.agentObservation;
  if (!observation) return "";
  const actions = snapshot.legalActions.map((action) => action.agentAction).join(", ");
  return ` (${observation.phase ?? "lane_choice"}: ${actions})`;
}

/** Polls the live Game every frame; only reacts when the snapshot changes, throttled to minIntervalMs. */
export class AiAgentStateReader {
  constructor({
    getGame = () => window._gear_currentGame,
    session = null,
    logChanges = true,
    minIntervalMs = 100,
  } = {}) {
    this.getGame = getGame;
    this.session = session;
    this.logChanges = logChanges;
    this.minIntervalMs = minIntervalMs;
    this.snapshot = null;
    this.lastFingerprint = null;
    this.lastEmittedAt = -Infinity;
    this.listeners = new Set();
    this.rafId = null;
    this.opportunities = new OpportunityTracker();
  }

  start() {
    if (this.rafId !== null) return;

    const tick = () => {
      this.snapshot = readGameSnapshot(this.getGame(), this.session, this.opportunities);
      const fingerprint = snapshotFingerprint(this.snapshot);
      const now = performance.now();
      if (fingerprint !== this.lastFingerprint && now - this.lastEmittedAt >= this.minIntervalMs) {
        this.lastFingerprint = fingerprint;
        this.lastEmittedAt = now;
        if (this.logChanges) console.log(`[aiAgent] state changed${choiceLabel(this.snapshot)}`, this.snapshot);
        this.listeners.forEach((listener) => listener(this.snapshot));
      }
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  stop() {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  }

  getSnapshot() {
    return this.snapshot;
  }

  /** @returns {number | null} performance.now() at which this choice opened, while it's still open */
  getChoiceOnsetMs(opportunityId) {
    return this.opportunities.onsetFor(opportunityId);
  }

  /** @param {(snapshot: object | null) => void} listener @returns {() => void} unsubscribe */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
